import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import i18n from '@/i18n';
import formatDate from '@/helpers/formatDate';
import {
  AssetAssignmentErrorDetailsSchema,
  IntegrationUnmappedErrorDetailsSchema,
  HRISOperationFailedErrorDetailsSchema,
  OrganizationCannotCreateProcurementRequestErrorDetailsSchema,
  MissingMDMEntitlementErrorDetailsSchema,
  OutdatedProductPriceErrorDetailsSchema,
  UnsupportedCountryCodesErrorDetailsSchema,
  InvalidPayOverTimeSettingsErrorDetailsSchema,
  StripeTransactionFailedErrorDetailsSchema,
  EmployeeLeaveOverlapErrorDetailsSchema,
} from '@electricjs/core_entity-client';

/**
 * Type predicate to narrow an unknown error to `FetchBaseQueryError`
 */
export function isFetchBaseQueryError(
  error: unknown
): error is FetchBaseQueryError {
  return (
    typeof error === 'object' &&
    error !== null &&
    error !== undefined &&
    'status' in error
  );
}

export function getBaseQueryErrorMessage(error: unknown): string {
  const fallbackMessage = i18n.t('common:errors.somethingWentWrong');

  if (!isFetchBaseQueryError(error)) {
    return fallbackMessage;
  }

  if ('error' in error) {
    return error.error;
  }

  if (!('data' in error)) {
    return fallbackMessage;
  }

  const errorData = error.data;

  // New format with error.code
  if (
    typeof errorData === 'object' &&
    errorData !== null &&
    'error' in errorData
  ) {
    const result = handleErrorCode(errorData.error, fallbackMessage);
    if (result !== null) {
      return result;
    }
  }

  // Legacy format (deprecated)
  const legacyResult = handleLegacyError(errorData);
  if (legacyResult !== null) {
    return legacyResult;
  }

  return fallbackMessage;
}

/**
 * Extracts fields from error schemas
 * Enforces the contract with the backend via typed schemas
 */
function transformErrorDetails(
  errorCode: string,
  details: unknown
): Record<string, string | number> {
  if (!details) {
    return {};
  }

  switch (errorCode) {
    case 'assets.asset_assignment_error': {
      const d = details as AssetAssignmentErrorDetailsSchema;
      return {
        count: d.asset_names.length,
        asset_names: d.asset_names.join(', '),
      };
    }

    case 'integrations.unmapped_error': {
      const d = details as IntegrationUnmappedErrorDetailsSchema;
      return {
        integration_name: d.integration_name,
        error_message: d.error_message,
      };
    }

    case 'organizations.hris_operation_failed': {
      const d = details as HRISOperationFailedErrorDetailsSchema;
      return {
        error_message: d.error_message,
      };
    }

    case 'procurement.organization_cannot_create_procurement_request': {
      const d =
        details as OrganizationCannotCreateProcurementRequestErrorDetailsSchema;
      return {
        organization_name: d.organization_name,
      };
    }

    case 'procurement.missing_mdm_entitlement': {
      const d = details as MissingMDMEntitlementErrorDetailsSchema;
      return {
        employee_name: d.employee_name,
        required_entitlement: d.required_entitlement,
      };
    }

    case 'procurement.outdated_product_price': {
      const d = details as OutdatedProductPriceErrorDetailsSchema;
      return {
        product_name: d.product_name,
        provided_price: d.provided_price,
        expected_price: d.expected_price,
      };
    }

    case 'procurement.unsupported_country_codes': {
      const d = details as UnsupportedCountryCodesErrorDetailsSchema;
      return {
        country_codes: d.unsupported_country_codes.join(', '),
        products: d.product_names.join(', '),
      };
    }

    case 'procurement.invalid_pay_over_time_settings': {
      const d = details as InvalidPayOverTimeSettingsErrorDetailsSchema;
      return {
        transaction_source_type: d.transaction_source_type,
        interval_count: d.interval_count,
      };
    }

    case 'procurement.stripe_transaction_failed': {
      const d = details as StripeTransactionFailedErrorDetailsSchema;
      return {
        error_message: d.error_message,
      };
    }

    case 'organizations.employee_leave_overlap': {
      const d = details as EmployeeLeaveOverlapErrorDetailsSchema;
      const returnDate: string | null = d.existing_leave_return_date;
      return {
        start_date: formatDate(d.existing_leave_start_date),
        return_date: returnDate
          ? formatDate(returnDate)
          : i18n.t('leave:steps.review.openEnded'),
      };
    }

    default:
      // For errors without UI fields, return empty object
      return {};
  }
}

/**
 * Handles new error format with error.code
 * Format: { error: { code: string, message?: string, details?: object } }
 */
function handleErrorCode(
  errorPayload: unknown,
  fallbackMessage: string
): string | null {
  if (
    typeof errorPayload === 'object' &&
    errorPayload !== null &&
    'code' in errorPayload &&
    typeof errorPayload.code === 'string'
  ) {
    const translationKey = `core-entity:errors.${errorPayload.code}`;

    // Translation exists → use translated text
    if (i18n.exists(translationKey)) {
      const details = 'details' in errorPayload ? errorPayload.details : {};
      const transformedDetails = transformErrorDetails(
        errorPayload.code,
        details
      );
      return i18n.t(translationKey, transformedDetails);
    }

    // No translation → use backend message if provided
    if ('message' in errorPayload && typeof errorPayload.message === 'string') {
      return errorPayload.message;
    }

    // No translation and no backend message → generic fallback
    return fallbackMessage;
  }

  return null;
}

/**
 * Handles legacy error formats (deprecated)
 * Formats: string | { detail: string }
 */
function handleLegacyError(errorData: unknown): string | null {
  if (typeof errorData === 'string') {
    return errorData;
  }

  if (
    typeof errorData === 'object' &&
    errorData &&
    'detail' in errorData &&
    typeof errorData.detail === 'string'
  ) {
    return errorData.detail;
  }

  return null;
}
